import { ChangeEvent, useLayoutEffect, useRef } from "react";
import { TranslationKey } from "../../../../shared/i18n/i18n";
import { FieldVisibility } from "../../common/FieldVisibilityIcon";
import { AdminDetailLabel } from "./AdminDetailLabel";
import { AdminQuotaCounter } from "./AdminQuotaCounter";

type AdminTranslationTextFieldProps = {
  availableCharacterCount?: number;
  isReadOnly: boolean;
  label: string;
  onChange: (value: string) => void;
  t: (key: TranslationKey) => string;
  value: string;
  visibility: FieldVisibility;
};

export const AdminTranslationTextField = ({
  availableCharacterCount,
  isReadOnly,
  label,
  onChange,
  t,
  value,
  visibility,
}: AdminTranslationTextFieldProps) => {
  const editorBodyRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const lineNumberRefs = useRef<Array<HTMLDivElement | null>>([]);
  const mirrorRef = useRef<HTMLDivElement | null>(null);
  const logicalLines = value ? value.split(/\r\n|\r|\n/) : [""];

  useLayoutEffect(() => {
    const editorBody = editorBodyRef.current;
    const textarea = textareaRef.current;
    const mirror = mirrorRef.current;
    if (!editorBody || !textarea || !mirror) {
      return;
    }

    const syncLayout = () => {
      textarea.style.height = "0px";
      textarea.style.height = `${textarea.scrollHeight}px`;

      const measuredLineHeights = Array.from(
        mirror.querySelectorAll<HTMLElement>("[data-line-height-source]"),
        (lineElement) => lineElement.getBoundingClientRect().height,
      );

      lineNumberRefs.current.forEach((lineNumberElement, index) => {
        if (!lineNumberElement) {
          return;
        }
        const nextHeight = measuredLineHeights[index];
        lineNumberElement.style.height = nextHeight ? `${nextHeight}px` : "";
      });
    };

    syncLayout();

    const resizeObserver = new ResizeObserver(() => {
      syncLayout();
    });
    resizeObserver.observe(editorBody);

    return () => {
      resizeObserver.disconnect();
    };
  }, [logicalLines, value, isReadOnly]);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(event.target.value);
  };

  return (
    <div className="admin-detail-field">
      <AdminDetailLabel
        label={label}
        t={t}
        visibility={visibility}
      />
      <div className="admin-detail-translation-editor">
        <div
          aria-hidden="true"
          className="admin-detail-translation-gutter"
        >
          {logicalLines.map((_line, index) => (
            <div
              className="admin-detail-translation-line-number"
              key={index + 1}
              ref={(element) => {
                lineNumberRefs.current[index] = element;
              }}
            >
              {index + 1}
            </div>
          ))}
        </div>
        <div
          className="admin-detail-translation-editor-body"
          ref={editorBodyRef}
        >
          <div
            aria-hidden="true"
            className="admin-detail-translation-mirror"
            ref={mirrorRef}
          >
            {logicalLines.map((line, index) => (
              <div
                className="admin-detail-translation-mirror-line"
                data-line-height-source
                key={index}
              >
                {line || " "}
              </div>
            ))}
          </div>
          <textarea
            className="admin-detail-translation-textarea"
            onChange={handleChange}
            readOnly={isReadOnly}
            ref={textareaRef}
            rows={logicalLines.length}
            spellCheck={false}
            value={value}
          />
        </div>
      </div>
      {!value ? <p className="admin-detail-translation-empty">{t("view.noContent")}</p> : null}
      {typeof availableCharacterCount === "number" ? (
        <AdminQuotaCounter
          available={availableCharacterCount}
          current={value.length}
        />
      ) : null}
    </div>
  );
};
